import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NoopValueAccessorDirective } from '../facility-select/noop-value-accessor.directive';
import {
  EquipmentModelClient,
  SearchEquipmentModelsRequest,
} from '../../../core/openapi/bff-admin-api';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  scan,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

type EquipmentModelsRequest = Required<
  Pick<SearchEquipmentModelsRequest, 'skip' | 'take' | 'includeTotalCount'>
> &
  Pick<SearchEquipmentModelsRequest, 'keyword'>;

/** Страница ответа вместе с признаком того, чем она является: первой или догруженной. */
interface IEquipmentModelPage {
  isFirstPage: boolean;
  batch: IOptionItem[];
}

@Component({
  selector: 'mon-equipment-model-select',
  templateUrl: './equipment-model-select.component.html',
  styleUrl: './equipment-model-select.component.scss',
  imports: [ReactiveFormsModule, SelectComponent, AsyncPipe],
  hostDirectives: [NoopValueAccessorDirective],
})
export class EquipmentModelSelectComponent implements OnInit {
  private readonly equipmentModelClient = inject(EquipmentModelClient);

  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  private readonly destroyRef = inject(DestroyRef);

  @Input()
  public label: string | null = null;

  @Input()
  public labelTranslateKey: string | null = null;

  @Input()
  public searchTranslateKey = 'monitoring.search';

  @Input()
  public fullWidth = false;

  private loadedCount = 0;
  private totalCount = 0;
  private readonly pageSize = 10 as const;
  private readonly keywordDebounceMs = 500 as const;

  /**
   * Стартуем в состоянии загрузки: первый запрос уходит синхронно при подписке шаблона,
   * и начальное `false` пришлось бы менять прямо во время рендера.
   */
  protected readonly isLoading = signal(true);

  private readonly keyword$ = new Subject<string>();

  public get control(): FormControl {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-facility-select must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-facility-select supports only FormControl');
    }

    return control;
  }

  private initialRequest: EquipmentModelsRequest = {
    skip: 0,
    take: this.pageSize,
    includeTotalCount: true,
  };

  private readonly request$ = new BehaviorSubject<EquipmentModelsRequest>(this.initialRequest);

  public readonly equipmentModelOptions: Observable<IOptionItem[]> = this.request$.pipe(
    tap((): void => this.isLoading.set(true)),
    switchMap((request) =>
      this.equipmentModelClient.v1EquipmentModelSearchPost(request).pipe(
        tap((res) => {
          if (res.totalCount != null) {
            this.totalCount = res.totalCount;
          }
        }),
        map(
          (response): IEquipmentModelPage => ({
            isFirstPage: request.skip === 0,
            batch: (response.items ?? []).map<IOptionItem>((equipmentModel) => ({
              id: equipmentModel.id,
              label: equipmentModel.name,
            })),
          }),
        ),
      ),
    ),
    tap((): void => this.isLoading.set(false)),
    scan((acc: IOptionItem[], { isFirstPage, batch }: IEquipmentModelPage): IOptionItem[] => {
      // Новый поиск начинает список с нуля, догрузка страницы — дополняет накопленный.
      const merged = isFirstPage ? [] : [...acc];
      const seen = new Set(merged.map((x) => x.id));

      for (const item of batch) {
        if (!item.id) continue;
        if (seen.has(item.id)) continue;

        seen.add(item.id);
        merged.push(item);
      }

      this.loadedCount = merged.length;

      return merged;
    }, []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    this.keyword$
      .pipe(
        debounceTime(this.keywordDebounceMs),
        map((keyword: string): string => keyword.trim()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((keyword: string): void => {
        this.loadedCount = 0;
        this.totalCount = 0;

        this.request$.next({ ...this.initialRequest, keyword });
      });
  }

  public loadMore(): void {
    if (this.isLoading()) {
      return;
    }

    if (this.loadedCount >= this.totalCount) {
      return;
    }

    const current = this.request$.value;

    this.request$.next({
      ...current,
      skip: current.skip + this.pageSize,
      includeTotalCount: false,
    });
  }

  public onKeywordChange(keyword: string): void {
    this.keyword$.next(keyword);
  }
}
